import { useState, useCallback, useEffect, useRef } from "react";

/** Lightweight text-to-speech hook using the Web Speech API */
export function useTTS() {
  const [speaking, setSpeaking] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const stop = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    setActiveId(null);
    utteranceRef.current = null;
  }, []);

  const speak = useCallback(
    (id: string, text: string) => {
      if (!window.speechSynthesis) return;

      // If already speaking this article, stop
      if (activeId === id) {
        stop();
        return;
      }

      // Stop any current speech
      window.speechSynthesis.cancel();

      const utt = new SpeechSynthesisUtterance(text);
      utt.rate = 1;
      utt.pitch = 1;

      // Prefer a natural-sounding voice
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(
        (v) => v.lang.startsWith("en") && v.name.toLowerCase().includes("natural")
      ) ?? voices.find((v) => v.lang.startsWith("en"));
      if (preferred) utt.voice = preferred;

      utt.onend = () => {
        setSpeaking(false);
        setActiveId(null);
      };
      utt.onerror = () => {
        setSpeaking(false);
        setActiveId(null);
      };

      utteranceRef.current = utt;
      setSpeaking(true);
      setActiveId(id);
      window.speechSynthesis.speak(utt);
    },
    [activeId, stop]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  return { speak, stop, speaking, activeId };
}
